import { getNestedObject } from "../utils/helpers"

interface SchemaElement {
  _key: string
  _updateCreator: (value: any) => { key: string; value: any }
  _reducer: (state: any, action: { key: string; value: any }) => any
}

type Schema = Record<string, any>

const processSchema = (schema: Schema): any => {
  const intialState: any = {}
  processSubSchema("", schema, intialState)
  return intialState
}

const processSubSchema = (
  parentKey: string,
  schema: Schema,
  initialState: any
): void => {
  if (typeof schema !== "object" || schema === null) {
    return
  }

  Object.getOwnPropertyNames(schema)
    .filter(prop => !prop.startsWith("_"))
    .forEach(prop => {
      // Copy schema initial state to a new initial state object to enable auto-complete in IDE
      initialState[prop] = createInitialValue(schema[prop])
      const key = createKey(parentKey, prop)
      // Convert schema initial state to schema
      const element = createSchemaElement(prop, parentKey, key)
      processSubSchema(key, schema[prop], initialState[prop])
      schema[prop] = merge(schema[prop], element)
    })
}

const createKey = (parentKey: string, key: string): string => {
  return !parentKey ? key : `${parentKey}.${key}`
}

const createSchemaElement = (
  name: string,
  parentKey: string,
  key: string
): SchemaElement => {
  return {
    _key: key,
    _updateCreator: (value: any) => {
      return { key: key, value: value }
    },
    _reducer: (state: any, action: { key: string; value: any }) => {
      const newState = { ...state }
      if (!parentKey) {
        // Root level property - directly merge
        newState[name] = merge(state[name], action.value)
      } else {
        // Nested property - need to clone parent path
        const pathSegments = parentKey.split(".")
        let current = newState
        for (let i = 0; i < pathSegments.length; i++) {
          const segment = pathSegments[i]
          current[segment] = { ...current[segment] }
          if (i < pathSegments.length - 1) {
            current = current[segment]
          }
        }
        const parent = getNestedObject(newState, parentKey)
        parent[name] = merge(parent[name], action.value)
      }
      return newState
    },
  }
}

const merge = (parent: any, element: any): any => {
  if (typeof parent === "object") {
    return { ...parent, ...element }
  } else {
    return element
  }
}

const createInitialValue = (initialValue: any): any => {
  if (typeof initialValue !== "object") {
    return initialValue
  } else {
    return { ...initialValue }
  }
}

export default processSchema
export type { SchemaElement, Schema }
